// Source-tenant group targeting API surface (FD-113). Consumes the two manager
// read endpoints — the contract rows in docs/TASKS.md (FD-113 v1.0) are the
// authoritative shapes; the mirrored interfaces live in src/types/sourceGroups.ts.
//
//   GET /v1/migrations/{id}/source-groups                       — group search
//   GET /v1/migrations/{id}/source-groups/{groupId}/devices     — expand + serial join
//
// This is a discovery/resolution layer in front of the UNCHANGED add-devices
// flow: search a source-tenant group, expand it to its device members, and feed
// the resolved serial numbers to the existing serial-keyed
// POST /v1/migrations/{id}/devices. This module adds no write path.

import { ApiError, getJson } from './client';
import { ApiEnvelope, isApiEnvelope } from './migrations';
import { GroupDevicesResponse, SourceGroupsResponse } from '../types/sourceGroups';

// The consent-class 409 (FD-096 shape) and the whole-request 502 GRAPH_ERROR
// are byte-identical to the identity / source-device endpoints, so their
// recognisers are reused verbatim — one renderer (ConsentMessageBar) and one
// transient-error path across every Graph-backed read. For group targeting the
// drift 409 names `GroupMember.Read.All` (net-new per FD-119) as the missing
// role until each source tenant re-consents.
export { extractConsentError, isGraphError } from './identity';

// Server contract (FD-113 v1.0): `search` is required, trimmed, min 3 chars
// (large-tenant guard, mirrors source-devices / tenant-users). Callers gate on
// this before fetching. Reuses the FD-112 constant so the min-length rule stays
// single-sourced.
export { MIN_SEARCH_LENGTH } from './sourceDevices';

export interface SearchSourceGroupsParams {
  search: string;
  top?: number;
  skipToken?: string | null;
}

export async function searchSourceGroups(
  migrationId: string,
  params: SearchSourceGroupsParams,
): Promise<SourceGroupsResponse> {
  const search = new URLSearchParams();
  search.set('search', params.search);
  if (params.top) search.set('top', String(params.top));
  if (params.skipToken) search.set('skipToken', params.skipToken);
  const path =
    `/v1/migrations/${encodeURIComponent(migrationId)}/source-groups?${search.toString()}`;
  const envelope = await getJson<ApiEnvelope<SourceGroupsResponse>>(path);
  return envelope.data;
}

export interface GetGroupDevicesParams {
  // Direct members (`false`, default) vs `/transitiveMembers` (`true`, nested
  // groups flattened). Default direct — matches how Intune assignment targeting
  // is typically authored (FD-113 §3 / OQ-2).
  transitive?: boolean;
  top?: number;
  skipToken?: string | null;
}

export async function getGroupDevices(
  migrationId: string,
  groupId: string,
  params: GetGroupDevicesParams = {},
): Promise<GroupDevicesResponse> {
  const search = new URLSearchParams();
  if (params.transitive) search.set('transitive', 'true');
  if (params.top) search.set('top', String(params.top));
  if (params.skipToken) search.set('skipToken', params.skipToken);
  const qs = search.toString();
  const path =
    `/v1/migrations/${encodeURIComponent(migrationId)}` +
    `/source-groups/${encodeURIComponent(groupId)}/devices` +
    (qs ? `?${qs}` : '');
  const envelope = await getJson<ApiEnvelope<GroupDevicesResponse>>(path);
  return envelope.data;
}

// The non-consent machine codes these endpoints return (FD-113 v1.0):
//   400 D2C_HAS_NO_SOURCE_TENANT — D2C migration has no source tenant (should
//       not occur — the group tab is T2T-only — but handled defensively)
//   400 (other)                 — bad params (short search, non-int top, bad
//       transitive)
//   404 GROUP_NOT_FOUND         — the group no longer exists in the source
//       tenant (distinct from the unknown-migration 404)
//   404 (other)                 — unknown migration
//   502 GRAPH_ERROR             — token-acquisition, member-read, or serial-join
//       failure after retry
// Consent-class 409s are recognised separately via extractConsentError (reused
// from identity.ts) so they render the ConsentMessageBar, not a raw error.
export type SourceGroupErrorCode =
  | 'D2C_HAS_NO_SOURCE_TENANT'
  | 'GROUP_NOT_FOUND'
  | 'INVALID_PARAMS'
  | 'MIGRATION_NOT_FOUND'
  | 'GRAPH_ERROR'
  | 'UNKNOWN';

export function sourceGroupErrorCode(err: unknown): SourceGroupErrorCode {
  if (!(err instanceof ApiError)) return 'UNKNOWN';

  const envelopeCode = readEnvelopeErrorCode(err.body);
  if (envelopeCode === 'D2C_HAS_NO_SOURCE_TENANT') return 'D2C_HAS_NO_SOURCE_TENANT';
  if (envelopeCode === 'GROUP_NOT_FOUND') return 'GROUP_NOT_FOUND';
  if (envelopeCode === 'GRAPH_ERROR') return 'GRAPH_ERROR';

  switch (err.status) {
    case 400:
      return 'INVALID_PARAMS';
    case 404:
      return 'MIGRATION_NOT_FOUND';
    case 502:
      return 'GRAPH_ERROR';
    default:
      return 'UNKNOWN';
  }
}

function readEnvelopeErrorCode(body: unknown): string | undefined {
  if (!isApiEnvelope(body)) return undefined;
  const code = (body as ApiEnvelope<unknown>).error?.code;
  return typeof code === 'string' ? code : undefined;
}
